'use client';

import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useMemo
} from 'react';
import type { GeocodeLocation } from '~/contracts/vm';

export type ContextType = {
  currentZip: string;
  geoCoords: GeocodeLocation | undefined;
  mapWrapperRef: React.RefObject<HTMLDivElement | null>;
  setCurrentZip: (zip: string) => void;
  setGeoCoords: (coords: GeocodeLocation | undefined) => void;
};

const defaultContext: ContextType = {
  currentZip: '',
  geoCoords: undefined,
  mapWrapperRef: { current: null },
  setCurrentZip() {},
  setGeoCoords() {}
};

const GlobalContext = createContext<ContextType>(defaultContext);

export type OwnProps = React.PropsWithChildren<{}>;

export function GlobalContextProvider(props: OwnProps) {
  const [currentZip, setCurrentZip] = useState<string>('');
  const [geoCoords, setGeoCoords] = useState<GeocodeLocation | undefined>(
    undefined
  );
  const mapWrapperRef = useRef<HTMLDivElement | null>(null);

  const value: ContextType = useMemo<ContextType>(
    () => ({
      currentZip,
      geoCoords,
      mapWrapperRef,
      setCurrentZip,
      setGeoCoords
    }),
    [currentZip, geoCoords]
  );

  return (
    <GlobalContext.Provider value={value}>
      {props.children}
    </GlobalContext.Provider>
  );
}

GlobalContextProvider.displayName = 'GlobalContextProvider';

export const useGlobalContext = () => useContext(GlobalContext);

export default GlobalContext;
